import React from 'react';

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon: React.ReactNode;
  loading?: boolean;
  className?: string;
  valuePrefix?: string;
  valueSuffix?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  change, 
  changeLabel, 
  icon, 
  loading = false,
  className = '',
  valuePrefix = '',
  valueSuffix = ''
}) => {
  const formatValue = (val: string | number) => {
    if (typeof val === 'number') {
      if (val >= 1000000) {
        return (val / 1000000).toFixed(1) + 'M';
      } else if (val >= 1000) {
        return (val / 1000).toFixed(1) + 'K';
      }
      return val.toLocaleString();
    }
    return val;
  };

  const getChangeColor = (changeValue?: number) => {
    if (!changeValue) return 'text-meta-6';
    return changeValue >= 0 ? 'text-meta-3' : 'text-meta-1';
  };

  const getChangeIcon = (changeValue?: number) => {
    if (!changeValue) return null;
    if (changeValue >= 0) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
        </svg>
      );
    }
  };

  if (loading) {
    return (
      <div className={`rounded-sm border border-stroke bg-white py-6 px-7.5 shadow-default dark:border-strokedark dark:bg-boxdark ${className}`}>
        <div className="flex h-11.5 w-11.5 items-center justify-center rounded-full bg-meta-2 dark:bg-meta-4 mb-4">
          <div className="animate-pulse bg-gray-300 h-6 w-6 rounded"></div>
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-300 rounded w-3/4 mb-3"></div>
          <div className="h-8 bg-gray-300 rounded w-1/2 mb-2"></div>
          <div className="h-3 bg-gray-300 rounded w-1/3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-sm border border-stroke bg-white py-6 px-7.5 shadow-default dark:border-strokedark dark:bg-boxdark ${className}`}>
      <div className="flex h-11.5 w-11.5 items-center justify-center rounded-full bg-meta-2 dark:bg-meta-4 mb-4">
        {icon}
      </div>

      <div className="mt-4 flex items-end justify-between">
        <div>
          <h4 className="text-title-md font-bold text-black dark:text-white">
            {valuePrefix}{formatValue(value)}{valueSuffix}
          </h4>
          <span className="text-sm font-medium text-meta-6 dark:text-meta-6">
            {title}
          </span>
        </div>

        {change !== undefined && (
          <span className={`flex items-center gap-1 text-sm font-medium ${getChangeColor(change)}`}>
            {Math.abs(change)}%
            {getChangeIcon(change)}
          </span>
        )}
      </div>

      {changeLabel && (
        <div className="mt-2">
          <span className="text-xs font-medium text-meta-6">
            {changeLabel}
          </span>
        </div>
      )}
    </div>
  );
};

export default MetricCard;